import { Component, EventEmitter, OnInit } from '@angular/core';
import { FolderService } from '../folder.service';
import { Folder } from '../folder';
import { NotificationService } from '../../../shared/notification.service';
import { AuthService } from '../../../auth/auth.service';

@Component({
  selector   : 'app-folder-list',
  templateUrl: 'list.component.html',
  styleUrls  : [],
})
export class FolderListComponent implements OnInit {
  public folder: Folder;

  /**
   * A list of all the folders currently loaded.
   *
   * @type {Folder[]}
   */
  public folders: Folder[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of folders.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of folders that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of folders that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a folder has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public folderDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {FolderService} folderService
   * @param {NotificationService} notificationService
   */
  constructor(
    private folderService: FolderService,
    private notificationService: NotificationService,
    private authService: AuthService,
  )
  {
    this.folderService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.folderService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.folderService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.folderService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the folder list.
   */
  public ngOnInit(): void {
    this.loadFolders(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * folder list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadFolders(pageNumber);
  }

  /**
   * Loads a list of folders using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadFolders(page?: number, limit?: number): void {
    const listFolders = this.folderService
      .listFolders(page, limit)
      .subscribe((folders: Folder[]) => {
        this.folders = folders;
        listFolders.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate folder ID
   * to delete the folder.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.folderService.deleteFolder(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Folder Deleted`,
          `Folder with ID ${id} was deleted.`,
          5,
        );
        this.folderDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Folder Not Deleted`,
          err.error.message,
          10,
        );
        this.folderDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
